/**
 * 商品カードスライダー（無限ループ対応）
 *
 * クローンスライド技法:
 *   トラック構成: [clone-last] [real-0] ... [real-(N-1)] [clone-first]
 *   最後→最初: clone-first までアニメ → 瞬時に real-0 へスナップ
 *   最初→最後: clone-last までアニメ → 瞬時に real-(N-1) へスナップ
 *
 * - PC：ホバー時に矢印フェードイン → クリックでスライド
 * - SP：タッチスワイプでスライド
 * - カラースウォッチと連携（goTo API）
 */

let cardSliderResizeRaf = null;

function scheduleAllCardSlidersLayout() {
  if (cardSliderResizeRaf != null) return;
  cardSliderResizeRaf = requestAnimationFrame(() => {
    cardSliderResizeRaf = null;
    document.querySelectorAll("[data-card-slider]").forEach((el) => {
      const slider = el._cardSlider;
      if (!slider || slider.total <= 1) return;
      slider._syncSlideWidths();
      slider._setPosition(slider.current, false);
    });
  });
}

class CardSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector("[data-card-slider-track]");
    this.total = parseInt(el.dataset.total, 10) || 1;
    this.current = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isSwiping = false;
    this._looping = false;
    this._slideWidth = 0;

    // スライダーインスタンスをDOM要素に紐づけ（スウォッチ連携用）
    this.el._cardSlider = this;

    if (this.total > 1) {
      this._setupClones();
      this._syncSlideWidths();
      this._setPosition(0, false);
      this.bindEvents();
      requestAnimationFrame(() => {
        this._syncSlideWidths();
        this._setPosition(this.current, false);
      });
    }
  }

  /** ビューポート幅に合わせスライド幅を px 固定し、% translate のサブピクセルずれを防ぐ */
  _syncSlideWidths() {
    const w = this.el.clientWidth;
    if (!w) return;
    this._slideWidth = w;
    this.track.querySelectorAll(".card-slider__slide").forEach((slide) => {
      slide.style.flex = `0 0 ${w}px`;
      slide.style.width = `${w}px`;
    });
  }

  // 両端にクローンスライドを追加
  _setupClones() {
    const slides = Array.from(
      this.track.querySelectorAll("[data-card-slider-slide]"),
    );
    const firstClone = slides[0].cloneNode(true);
    const lastClone = slides[slides.length - 1].cloneNode(true);
    // クローンは data 属性を持たせない（goTo 検索対象外にする）
    firstClone.removeAttribute("data-card-slider-slide");
    lastClone.removeAttribute("data-card-slider-slide");
    this.track.appendChild(firstClone); // 末尾: clone-first
    this.track.insertBefore(lastClone, slides[0]); // 先頭: clone-last
    // トラック構成: [clone-last][real-0]...[real-N-1][clone-first]
  }

  // DOMインデックス = 論理インデックス + 1（先頭クローン分オフセット）
  _setPosition(index, animate = true) {
    const domIndex = index + 1;
    const w = this.el.clientWidth || this._slideWidth;
    if (!w) return;
    const px = Math.round(domIndex * w);
    if (!animate) {
      this.track.style.transition = "none";
      this.track.style.transform = `translate3d(-${px}px, 0, 0)`;
      void this.track.offsetWidth; // リフローを強制してトランジション無効を確定
      this.track.style.transition = "";
    } else {
      this.track.style.transform = `translate3d(-${px}px, 0, 0)`;
    }
  }

  // 外部から任意スライドへジャンプ（カラースウォッチ連携）
  goTo(index) {
    this.current = ((index % this.total) + this.total) % this.total;
    this._setPosition(this.current, true);
  }

  prev() {
    if (this._looping) return;
    if (this.current === 0) {
      // clone-last（DOM index 0）へアニメ → 瞬時に real-last へスナップ
      this._looping = true;
      this.track.style.transform = "translate3d(0, 0, 0)";
      this.track.addEventListener(
        "transitionend",
        () => {
          this.current = this.total - 1;
          this._setPosition(this.current, false);
          this._looping = false;
        },
        { once: true },
      );
    } else {
      this.current -= 1;
      this._setPosition(this.current, true);
    }
  }

  next() {
    if (this._looping) return;
    if (this.current === this.total - 1) {
      // clone-first（DOM index total+1）へアニメ → 瞬時に real-first へスナップ
      this._looping = true;
      const w = this.el.clientWidth || this._slideWidth;
      if (w) {
        const px = Math.round((this.total + 1) * w);
        this.track.style.transform = `translate3d(-${px}px, 0, 0)`;
      }
      this.track.addEventListener(
        "transitionend",
        () => {
          this.current = 0;
          this._setPosition(this.current, false);
          this._looping = false;
        },
        { once: true },
      );
    } else {
      this.current += 1;
      this._setPosition(this.current, true);
    }
  }

  bindEvents() {
    // 矢印ボタン（card__inner 直下に配置、data-slider-id で紐づけ）
    const sliderId = this.el.id;
    const prevBtn = document.querySelector(
      `[data-slider-id="${sliderId}"].card-slider__arrow--prev`,
    );
    const nextBtn = document.querySelector(
      `[data-slider-id="${sliderId}"].card-slider__arrow--next`,
    );

    prevBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.prev();
    });

    nextBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.next();
    });

    // タッチスワイプ
    this.track.addEventListener(
      "touchstart",
      (e) => {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
        this.isSwiping = false;
      },
      { passive: true },
    );

    this.track.addEventListener(
      "touchmove",
      (e) => {
        const dx = Math.abs(e.touches[0].clientX - this.touchStartX);
        const dy = Math.abs(e.touches[0].clientY - this.touchStartY);
        if (dx > dy && dx > 8) this.isSwiping = true;
      },
      { passive: true },
    );

    this.track.addEventListener(
      "touchend",
      (e) => {
        if (!this.isSwiping) return;
        const diff = this.touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          diff > 0 ? this.next() : this.prev();
        }
        this.isSwiping = false;
      },
      { passive: true },
    );

    // 画像クリックで商品ページへ遷移（矢印以外）
    // CSS z-index に依存せず JS で確実に処理する
    const productUrl = this.el.dataset.productUrl;
    if (productUrl) {
      this.el.addEventListener("click", (e) => {
        // 矢印ボタンのクリックは無視
        if (e.target.closest(".card-slider__arrow")) return;
        // スワイプ後のクリックは無視
        if (this.isSwiping) return;
        // 修飾キー対応（Ctrl+クリックで新しいタブなど）
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          window.open(productUrl, "_blank");
        } else {
          window.location.href = productUrl;
        }
      });
    }
  }
}

let cardSliderWindowResizeBound = false;

// ページ内の全スライダーを初期化
function initCardSliders() {
  if (!cardSliderWindowResizeBound) {
    cardSliderWindowResizeBound = true;
    window.addEventListener("resize", scheduleAllCardSlidersLayout);
  }
  document
    .querySelectorAll("[data-card-slider]:not([data-slider-initialized])")
    .forEach((el) => {
      el.dataset.sliderInitialized = "true";
      new CardSlider(el);
    });
  scheduleAllCardSlidersLayout();
}

// global.js の ProductRecommendations から呼び出せるようにグローバル公開
window.initCardSliders = initCardSliders;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCardSliders);
} else {
  initCardSliders();
}
